if (!customElements.get('read-more-component')) {
  class ReadMoreComponent extends HTMLElement {
    constructor() {
      super();

      this.linesVisible = parseInt(this.dataset.lines) || 3;
      this.contentId = this.dataset.contentId;
      this.buttonId = this.dataset.buttonId;

      this.content = this.querySelector(`#${this.contentId}`);
      this.button = this.querySelector(`#${this.buttonId}`);
      this.moreText = this.button.querySelector('.read-more__button-text--more');
      this.lessText = this.button.querySelector('.read-more__button-text--less');

      this.isExpanded = false;
      this.isInline = this.dataset.buttonPosition === 'inline';
      this.animationDuration = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--duration-default')) || 300;

      this.init();
    }

    init() {
      this.content.style.setProperty('--lines-visible', this.linesVisible);

      if (!this.content.classList.contains('read-more__content--collapsed')) {
        this.content.classList.add('read-more__content--collapsed');
      }

      this.collapsedHeight = Math.ceil(this._getLineHeight() * this.linesVisible);

      this.content.style.height = this.collapsedHeight + 'px';

      if (this.isInline) this.updateInlinePosition();

      this.checkIfTruncated();

      this.button.addEventListener('click', this.toggle.bind(this));

      window.addEventListener('resize', debounce(this.handleResize.bind(this), 150));
    }

    checkIfTruncated() {
      const contentHeight = this.content.scrollHeight;
      const lineHeight = parseFloat(getComputedStyle(this.content).lineHeight);
      const maxHeight = lineHeight * this.linesVisible;

      if (contentHeight > maxHeight) {
        this.button.classList.remove('hidden');
      } else {
        this.button.classList.add('hidden');
      }
    }

    toggle() {
      this.isExpanded = !this.isExpanded;

      if (this.isExpanded) {
        this.expand();
      } else {
        this.collapse();
      }
    }

    expand() {
      const startHeight = this.collapsedHeight;
      const endHeight = this.content.scrollHeight;

      this.content.style.willChange = 'height';
      this.content.style.height = startHeight + 'px';

      this.content.classList.remove('read-more__content--collapsed');
      this.content.classList.add('read-more__content--expanded');

      this.content.offsetHeight;

      this.content.style.height = endHeight + 'px';

      setTimeout(() => {
        if (this.isExpanded) {
          this.content.style.height = 'auto';
        }
        this.content.style.willChange = '';
      }, this.animationDuration);

      if (this.isInline) this.classList.add('is-expanded');

      this.button.setAttribute('aria-expanded', 'true');

      this.moreText.classList.add('hidden');
      this.lessText.classList.remove('hidden');
    }

    collapse() {
      const startHeight = this.content.offsetHeight;

      this.content.style.willChange = 'height';

      this.content.classList.remove('read-more__content--expanded');

      this.content.style.height = startHeight + 'px';

      this.content.offsetHeight;

      this.content.style.height = this.collapsedHeight + 'px';

      setTimeout(() => {
        if (!this.isExpanded) {
          this.content.classList.add('read-more__content--collapsed');
          this.content.style.height = this.collapsedHeight + 'px';
        }
        this.content.style.willChange = '';
      }, this.animationDuration);

      if (this.isInline) this.classList.remove('is-expanded');

      this.button.setAttribute('aria-expanded', 'false');

      this.moreText.classList.remove('hidden');
      this.lessText.classList.add('hidden');
    }

    _getLineHeight() {
      const textEl = this.content.querySelector('p, div, li, span') || this.content;
      const computedStyle = getComputedStyle(textEl);
      const rawLineHeight = computedStyle.lineHeight;
      if (!rawLineHeight || rawLineHeight === 'normal') {
        return parseFloat(computedStyle.fontSize) * 1.2;
      }
      if (rawLineHeight.includes('px')) {
        return parseFloat(rawLineHeight);
      }
      return parseFloat(computedStyle.fontSize) * parseFloat(rawLineHeight);
    }

    updateInlinePosition() {
      const textEl = this.content.querySelector('p, div, li, span') || this.content;
      const textCs = getComputedStyle(textEl);
      this.style.setProperty('--last-line-top', (this.collapsedHeight - this._getLineHeight()) + 'px');
      this.button.style.lineHeight = textCs.lineHeight;
      this.button.style.fontSize = textCs.fontSize;
    }

    handleResize() {
      if (!this.isExpanded) {
        this.collapsedHeight = Math.ceil(this._getLineHeight() * this.linesVisible);
        this.content.style.height = this.collapsedHeight + 'px';
        this.checkIfTruncated();
      }

      if (this.isInline && !this.isExpanded) {
        this.updateInlinePosition();
      }
    }
  }

  customElements.define('read-more-component', ReadMoreComponent);
}
